// ─────────────────────────────────────────────────────────────────────────────
// RF-041 (S4, decisión D-2) — Tracker semanal de racha de 7 días (lunes a domingo).
//
// El RF pide el tracker en TODAS las vistas y hasta S4 solo existía en tres: Brillamos e Innovación
// (dentro de `StreakWidget`) y el Perfil (`Rp2Streak`). Faltaban Reconocer, Nominar y Fundamentales.
//
// La tira NO se copió a esas tres pantallas: se extrajo de `StreakWidget` a `StreakWeekStrip`, que
// es ahora la única implementación de los 7 días. Duplicarla habría dejado seis sitios donde
// equivocarse con la rotación del índice —`actividadSemanal` va de lunes a domingo y `Date.getDay()`
// de domingo a sábado—, que es exactamente el defecto que ya se corrigió una vez en el widget.
//
// `StreakWeekTracker` es el envoltorio autónomo para las pantallas que NO montan el widget completo.
// Lleva su propia superficie (`.streak-tracker-card`) porque la familia `.streak-*` está pintada
// para el fondo violeta oscuro del widget: colocada tal cual sobre el fondo claro de Reconocer o
// Nominar, el texto —blanco al 32-45 % de opacidad— sería ilegible.
//
// Sujeto al mismo gate de RF-043 que el resto (ver `useVisibleStreak`): con la racha apagada, este
// componente no pinta nada.
// ─────────────────────────────────────────────────────────────────────────────
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useVisibleStreak } from './useVisibleStreak';

/** Path exacto de `.bj-streak-star` (demo `BrillamosJuntosPage.tsx` : 204). */
const STAR_PATH = 'M50 8 C52 8 54 18 58 28 C60 33 65 35 70 36 L90 38 C93 38 94 40 92 42 L76 55 C73 58 72 62 73 66 L78 86 C79 89 77 91 74 89 L56 78 C53 76 47 76 44 78 L26 89 C23 91 21 89 22 86 L27 66 C28 62 27 58 24 55 L8 42 C6 40 7 38 10 38 L30 36 C35 35 40 33 42 28 C46 18 48 8 50 8 Z';

export function StreakStar({ className, color, light }: Readonly<{ className?: string; color: string; light: string }>) {
  const style = { '--streak-color': color, '--streak-light': light } as CSSProperties;
  return (
    <svg className={className} viewBox="0 0 100 100" style={style} aria-hidden="true">
      <path d={STAR_PATH} fill={light} />
    </svg>
  );
}

/** Semana completa L-D. El índice 0 es lunes, igual que `StreakDto.actividadSemanal`. Sábado y
 * domingo se pintan atenuados (`sdc--weekend`), como en el demo. */
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
/** Primer índice de fin de semana dentro de DAY_KEYS (sábado). */
const WEEKEND_FROM = 5;

/**
 * Glifo del círculo de un día SIN actividad, común a los dos trackers de RF-041: esta tira
 * (`StreakWeekStrip`, widget de Brillamos/Reconocer/Nominar) y la del Perfil (`Rp2Streak`, marcado
 * propio `.rp2-day` para su fondo claro). El día activo NO pasa por aquí: cada tracker pinta el suyo
 * con el recurso de su lenguaje visual (SVG `StreakStar` en el widget, emoji en el Perfil).
 *
 * Vive aquí y no duplicado en cada pantalla porque las dos venían divergiendo: el Perfil pintaba `·`
 * para HOY mientras esta tira usaba `·` para el fin de semana vacío y `✦` para HOY — el MISMO
 * carácter significaba dos cosas distintas según la pantalla, y en el Perfil se leía como un icono
 * que no cargó (el marcador real de hoy allá es el anillo índigo `.rp2-day--today`).
 */
export function streakDayFallbackMark(today: boolean, weekend: boolean): '✦' | '·' | '' {
  if (today) return '✦';
  if (weekend) return '·';
  return '';
}

/**
 * Los 7 días L–D de `StreakDto.actividadSemanal`. Única implementación del tracker de RF-041: la
 * consumen `StreakWidget` (Brillamos/Innovación) y `StreakWeekTracker` (Reconocer/Nominar/
 * Fundamentales).
 *
 * No consulta la racha por su cuenta a propósito — recibe la tira ya resuelta —: el widget ya tiene
 * los datos y una segunda llamada al hook dentro de él sería redundante.
 */
export function StreakWeekStrip({ week }: Readonly<{ week: boolean[] }>) {
  const { t } = useTranslation('recognition');
  const days = week.slice(0, 7);
  const activeDays = days.filter(Boolean).length;
  // Date.getDay() es 0=Dom..6=Sáb; `actividadSemanal` va de lunes a domingo, así que se rota.
  // Antes se mapeaba solo L-V y el fin de semana caía en -1: sábado y domingo perdían el marcador
  // de HOY y dejaban de atenuar los días futuros.
  const todayIdx = (new Date().getDay() + 6) % 7;

  return (
    <div className="streak-week-section">
      <div className="streak-week-top">
        <span className="streak-week-title">{t('streak.thisWeek')}</span>
        <span className="streak-week-progress">{activeDays}/7 {t('streak.days')}</span>
      </div>
      <div className="streak-days-row">
        {DAY_KEYS.map((key, i) => {
          const isActive = Boolean(days[i]);
          const isToday = i === todayIdx;
          const isFuture = i > todayIdx;
          const isWeekend = i >= WEEKEND_FROM;
          const cls = ['streak-day-circle', isActive ? 'sdc--active' : '', isToday && !isActive ? 'sdc--today' : '', isFuture ? 'sdc--future' : '', isWeekend ? 'sdc--weekend' : ''].filter(Boolean).join(' ');
          return (
            <div key={key} className="streak-day-col">
              <div className={cls}>
                {isActive && <StreakStar color="#a78bfa" light="#f0abfc" />}
                {!isActive && <span aria-hidden="true">{streakDayFallbackMark(isToday, isWeekend)}</span>}
              </div>
              <span className={`streak-day-name ${isToday ? 'streak-day-name--today' : ''}`}>
                {isToday ? t('streak.today') : t(`streak.day.${key}`)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tracker autónomo para las pantallas sin `StreakWidget`: Reconocer, Nominar y Fundamentales
 * (RF-041, decisión D-2).
 *
 * Solo la tira de 7 días, sin semanas/récord/total: esos tres números son el WIDGET de RF-042 y
 * repetirlos en cada pantalla del módulo no es lo que pide RF-041. Sin claves i18n nuevas — reusa
 * las que la tira ya traduce en los 7 idiomas.
 *
 * BUG (racha inconsistente, 2026-08-23) — mismo patrón que `StreakWidget`: sin `programa` la tira
 * era SIEMPRE de Fundamentales, aunque la pantalla que la monta sea de otro programa (Nominar es
 * SIEMPRE Brillamos Juntos). `NominarView` pasa `programa="Brillamos"`; Reconocer no pasa nada a
 * propósito — esa pantalla sí es Fundamentales, el día a día.
 */
export function StreakWeekTracker({ programa }: Readonly<{ programa?: string | null }> = {}) {
  const { t } = useTranslation('recognition');
  const streak = useVisibleStreak(programa);
  if (!streak) return null;

  return (
    <div className="streak-tracker-card">
      <div className="streak-tracker-head">
        <span className="streak-tracker-icon">
          <StreakStar color="#a78bfa" light="#f0abfc" />
        </span>
        <p className="streak-tracker-title">{t('streak.title')}</p>
      </div>
      <StreakWeekStrip week={streak.actividadSemanal} />
    </div>
  );
}

export default StreakWeekTracker;
